import { useContext, useRef, useState } from "react";
import { numberQueries } from "../../../../../config/filter/CustomFilterConfig";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "../../../../../context/FiltersContext";
import {
  QueryFilter,
  FirmaFilterKeys,
  FirmaFirmanummerSetting,
} from "../../../../../interfaces/filters";
import { f } from "../../../../../util/f";
import { AccordionFilterItem } from "../../../../accordion/Accordion";
import AppliedFilterItem from "../../../../filter/AppliedFilterItem";
import { ComboBox } from "../../../../filter/ComboBox";
import { NumberQuery } from "../../../../filter/custom/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../../select/Select";
import { FirmaDtoFilterInput } from "../../../../../interfaces/schema";

// helper
const filterKey: QueryFilter<FirmaFilterKeys>["filterKey"] = "firmanummer";

export default function FirmaFirmanummerFilter() {
  // query filter context
  const filters = useContext(FirmaFiltersContext);
  const updateFilters = useContext(FirmaFiltersEventContext);

  // find the filter settings
  const settings: FirmaFirmanummerSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // things
  const queries: NumberQuery[] = numberQueries;

  // refs
  const inputRef = useRef<HTMLInputElement>(null);
  const inputToRef = useRef<HTMLInputElement>(null);

  // state stuff
  const [selectedQuery, setSelectedQuery] = useState(queries[0]);
  const [isBetween, setIsBetween] = useState<boolean>(false);

  // handlers
  function addFilterEventHandler() {
    const inputValue = inputRef.current?.value;
    if (!inputValue) {
      return;
    }

    const key: FirmaFirmanummerSetting["key"] = `${
      selectedQuery.query
    }-${parseInt(inputValue)}`;

    if (settings.some((set) => set.key === key)) {
      alert("Filter er allerede aktivt.");
      return;
    }

    const settingsItem: FirmaFirmanummerSetting = {
      key: key,
      selectedQueryName: `${selectedQuery.displayName}`,
      displayName: `${parseInt(inputValue)}`,
      filter: { firmaNr: { [selectedQuery.query]: parseInt(inputValue) } },
    };

    const updatedSettings = [...settings, settingsItem];

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<FirmaFilterKeys, FirmaFirmanummerSetting[]> =
      {
        filterKey: filterKey,
        filter: filterString,
        parentFilter: "and",
        settings: updatedSettings,
      };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function addBetweenFilterEventHandler() {
    const inputFromValue = inputRef.current?.value;
    const inputToValue = inputToRef.current?.value;

    if (!inputFromValue || !inputToValue) {
      return;
    }
    const betweenItem: FirmaFirmanummerSetting = {
      key: `gt-${parseInt(inputFromValue)}-lt${parseInt(inputToValue)}`,
      selectedQueryName: "Mellem",
      displayName: `${parseInt(inputFromValue)} - ${parseInt(inputToValue)}`,
      filter: {
        and: [
          { firmaNr: { gt: parseInt(inputFromValue) } },
          { firmaNr: { lt: parseInt(inputToValue) } },
        ],
      },
    };

    const updatedSettings = [...settings, betweenItem];

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<FirmaFilterKeys, FirmaFirmanummerSetting[]> =
      {
        filterKey: filterKey,
        filter: filterString,
        parentFilter: "and",
        settings: updatedSettings,
      };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterEventHandler(key: FirmaFirmanummerSetting["key"]) {
    const updatedSettings = settings.filter((set) => set.key !== key);

    if (updatedSettings.length === 0) {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
      return;
    }

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<FirmaFilterKeys, FirmaFirmanummerSetting[]> =
      {
        filterKey: filterKey,
        filter: filterString,
        parentFilter: "and",
        settings: updatedSettings,
      };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }
  return (
    <AccordionFilterItem
      title="Firmanummer"
      value="firmanummer"
      isActive={settings.length > 0 && true}
    >
      <div className="c-applied-filter-group">
        {settings.map((set: FirmaFirmanummerSetting) => {
          return (
            <AppliedFilterItem
              key={set.key}
              labelPrefix={set.selectedQueryName}
              label={set.displayName}
              removeFilterHandler={() => {
                removeFilterEventHandler(set.key);
              }}
            />
          );
        })}
      </div>
      <ComboBox>
        <Select
          defaultValue={selectedQuery?.query}
          onValueChange={(value: string) => {
            if (value === "between") {
              setIsBetween(true);
              return;
            } else {
              setIsBetween(false);
            }

            const query = queries.find((q) => q.query === value);
            if (query) {
              setSelectedQuery(query);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg filterfunktion" badge />
          <SelectContent small>
            {queries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
            <SelectItem value={"between"} key={"between"}>
              Mellem
            </SelectItem>
          </SelectContent>
        </Select>
        <input
          className="form-control"
          type="number"
          placeholder="Firmanummer"
          ref={inputRef}
          required
          onKeyUp={(event) => {
            if (event.key === "Enter") {
              if (isBetween) {
                addBetweenFilterEventHandler();
              } else {
                addFilterEventHandler();
              }
            }
          }}
        />
        {isBetween && (
          <>
            -{" "}
            <input
              className="form-control"
              type="number"
              placeholder="Firmanummer"
              ref={inputToRef}
              required
              onKeyUp={(event) => {
                if (event.key === "Enter") {
                  if (isBetween) {
                    addBetweenFilterEventHandler();
                  } else {
                    addFilterEventHandler();
                  }
                }
              }}
            />
          </>
        )}
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1 btn--combo-action`}
          onClick={() => {
            if (isBetween) {
              addBetweenFilterEventHandler();
            } else {
              addFilterEventHandler();
            }
          }}
        >
          OK
        </button>
      </ComboBox>
    </AccordionFilterItem>
  );
}
